import { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

export type ScriptStatus = 'idle' | 'loading' | 'ready' | 'error';

export const useScript = (src: string, parentId?: string): ScriptStatus => {
  const hookId = useRef(
    `${parentId ? `${parentId} ` : ''}useScript[${uuidv4()}]`,
  );
  const [status, setStatus] = useState<ScriptStatus>(src ? 'loading' : 'idle');
  console.log(`${hookId.current} ENTER`);

  useEffect(() => {
    console.log(`${hookId.current}[useEffect] ENTER`);
    if (!src) {
      if (status !== 'idle') {
        console.log(
          `${hookId.current}[useEffect] setStatus(${status} -> idle)`,
        );
        setStatus('idle');
      }

      return;
    }

    let script = document.querySelector(
      `script[src="${src}"]`,
    ) as HTMLScriptElement;

    if (!script) {
      console.log(
        `${hookId.current}[useEffect] script does not exist, adding to document`,
      );
      script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.setAttribute('data-status', 'loading');
      document.body.appendChild(script);

      const setAttributeFromEvent = (event: Event) => {
        console.log(
          `${hookId.current}[setAttributeFromEvent] event.type=${event.type}`,
        );
        const newScriptStatus = event.type === 'load' ? 'ready' : 'error';
        script.setAttribute('data-status', newScriptStatus);
      };
      script.addEventListener('load', setAttributeFromEvent);
      script.addEventListener('error', setAttributeFromEvent);
    } else {
      console.log(`${hookId.current}[useEffect] script already exists`);
      const scriptStatus = script.getAttribute('data-status') as ScriptStatus;
      if (status !== scriptStatus) {
        console.log(
          `${hookId.current}[useEffect] setStatus(${status} -> ${scriptStatus})`,
        );
        setStatus(scriptStatus);
      }
    }

    const setStateFromEvent = (event: Event) => {
      console.log(
        `${hookId.current}[setStateFromEvent] event.type=${event.type}`,
      );
      const newScriptStatus = event.type === 'load' ? 'ready' : 'error';
      if (status !== newScriptStatus) {
        console.log(
          `${hookId.current}[setStateFromEvent] setStatus(${status} -> ${newScriptStatus})`,
        );
        setStatus(newScriptStatus);
      }
    };

    console.log(
      `${hookId.current}[useEffect] Will add event listeners that call setStateFromEvent`,
    );
    script.addEventListener('load', setStateFromEvent);
    script.addEventListener('error', setStateFromEvent);

    const thisHookId = hookId.current;

    return () => {
      if (script) {
        console.log(
          `${thisHookId}[useEffect] Will remove event listeners that call setStateFromEvent`,
        );
        script.removeEventListener('load', setStateFromEvent);
        script.removeEventListener('error', setStateFromEvent);
      }
    };
  }, [src, status]);

  return status;
};
